import { CheckCircleOutlined, CloseCircleOutlined, MobileOutlined, GlobalOutlined } from "@ant-design/icons";
import { Button, Card, Popconfirm, Table, TableColumnsType, TableProps } from "antd";
import axios from "axios";
import { message } from "@bindeleddet/core/utils";
import { Event, EventResponsibility, IsOpenType, Member, BLgroupMember } from "@bindeleddet/core/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { ResponsibilityType } from "containers/Admin/CreateOrEditEvent/CreateOrEditEvent";
import { isBlMemberUser, useLoginContext } from "@bindeleddet/core/contexts/login";
import { formatDateWithTimeNorwegian } from "helpers/dateUtils";

interface EventWithResponsible extends Event {
  mainResponsible?: string;
}

const EventsTable = () => {
  const history = useHistory();
  const { currentUser } = useLoginContext();
  const [events, setEvents] = useState<EventWithResponsible[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isItOrCompanyMember, setIsItOrCompanyMember] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  });

  const fetchEvents = useCallback(async (page = 1, pageSize = 5) => {
    setLoading(true);
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      time_filter: "future",
      ordering: "start",
    });

    try {
      const response = await axios.get<{ results: Event[]; count: number }>(
        `/events/admin-events/?${params.toString()}`
      );

      // Get main responsible for each event
      const eventsWithResponsible = await Promise.all(
        response.data.results.map(async event => {
          try {
            const responsibilitiesResponse = await axios.get<EventResponsibility[]>(
              `/events/${event.id}/event-responsibility/`
            );
            const mainResponsible = responsibilitiesResponse.data.find(
              r => r.responsibility_type === ResponsibilityType.MAIN_RESPONSIBLE
            );

            if (mainResponsible) {
              const memberResponse = await axios.get<Member>(`/internal/members/${mainResponsible.bl_member}/`);
              return { ...event, mainResponsible: memberResponse.data.full_name };
            }
            return { ...event, mainResponsible: undefined };
          } catch (error) {
            return { ...event, mainResponsible: undefined };
          }
        })
      );

      setEvents(eventsWithResponsible);
      setPagination(prev => ({
        ...prev,
        total: response.data.count,
      }));
    } catch (error) {
      message("error", "Kunne ikke hente arrangementer. Prøv igjen senere.");
    } finally {
      setLoading(false);
    }
  }, []);

  const { current, pageSize } = pagination;

  useEffect(() => {
    fetchEvents(current, pageSize);
  }, [fetchEvents, current, pageSize]);

  // Check if the user is a member of the IT group (group 1) or company group (group 5)
  useEffect(() => {
    if (!isBlMemberUser(currentUser)) return;
    axios.get<BLgroupMember[]>(`/internal/${currentUser.id}/groups/`).then(response => {
      setIsItOrCompanyMember(response.data.some(group => group.bl_group === 1 || group.bl_group === 5));
    });
  }, [currentUser]);

  // Check if there are any hidden events (is_visible === 0)
  const hasHiddenEvents = useMemo(() => {
    return events.some(event => event.is_visible === 0);
  }, [events]);

  const handleMakeVisible = async () => {
    try {
      await axios.post("/events/admin-events/make-future-hidden-visible/");
      message("success", "Alle fremtidige arrangementer er nå synliggjort.");
      // Update the events list
      fetchEvents(current, pageSize);
    } catch (error) {
      message("error", "Kunne ikke synliggjøre arrangementer. Prøv igjen senere.");
    }
  };

  const getStatusText = (isOpen: IsOpenType): string => {
    switch (isOpen) {
      case IsOpenType.NOT_OPENED:
        return "Ikke åpnet";
      case IsOpenType.OPENED:
        return "Åpen";
      case IsOpenType.DEREGISTERING_CLOSED:
        return "Åpen, avmelding stengt";
      case IsOpenType.CLOSED:
        return "Stengt";
      default:
        return "Ukjent";
    }
  };

  const getVisibilityIcon = (isVisible: number) => {
    if (isVisible === 0) {
      // Not visible
      return <CloseCircleOutlined style={{ color: "red", fontSize: 18 }} />;
    }
    if (isVisible === 3) {
      // Visible everywhere (both app and website)
      return <CheckCircleOutlined style={{ color: "green", fontSize: 18 }} />;
    }
    if (isVisible === 1) {
      // Only visible on website
      return <GlobalOutlined style={{ color: "orange", fontSize: 18 }} title="Kun synlig på nettside" />;
    }
    if (isVisible === 2) {
      // Only visible in app
      return <MobileOutlined style={{ color: "orange", fontSize: 18 }} title="Kun synlig i app" />;
    }
    return null;
  };

  type OnChange = NonNullable<TableProps<EventWithResponsible>["onChange"]>;

  const handleChange: OnChange = tablePagination => {
    setPagination(prev => ({
      ...prev,
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 5,
    }));
  };

  const onRowClick = (eventID: number) => ({
    onClick: () => {
      history.push(`/admin/events/${eventID}`);
    },
    style: {
      cursor: "pointer",
    },
  });

  const columns: TableColumnsType<EventWithResponsible> = [
    {
      title: "Dato",
      dataIndex: "start",
      key: "start",
      width: 180,
      render: (text: string) => {
        return formatDateWithTimeNorwegian(text);
      },
    },
    {
      title: "Tittel",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Ansvarlig",
      key: "mainResponsible",
      width: 200,
      render: (_, record) => {
        return record.mainResponsible || "Ikke bestemt";
      },
    },
    {
      title: "Status",
      dataIndex: "is_open",
      key: "is_open",
      width: 180,
      render: (isOpen: IsOpenType) => {
        return getStatusText(isOpen);
      },
    },
    {
      title: "Oppfylling",
      key: "fulfillment",
      width: 120,
      render: (_, record) => {
        return `${record.registrations || 0}/${record.seats || 0}`;
      },
    },
    {
      title: "Synlig",
      dataIndex: "is_visible",
      key: "is_visible",
      width: 100,
      align: "center" as const,
      render: (isVisible: number) => {
        return getVisibilityIcon(isVisible);
      },
    },
  ];

  return (
    <Card title="Kommende arrangementer">
      <Table
        columns={columns}
        dataSource={events}
        loading={loading}
        onRow={event => onRowClick(event.id)}
        onChange={handleChange}
        pagination={{
          ...pagination,
          showSizeChanger: false,
          pageSize: 5,
        }}
        rowKey="id"
      />
      {hasHiddenEvents && isItOrCompanyMember && (
        <div style={{ marginTop: 16 }}>
          <Popconfirm
            title={
              <>
                <div style={{ marginBottom: 8, fontWeight: "bold" }}>Synliggjør fremtidige arrangementer</div>
                <div style={{ fontWeight: "normal" }}>
                  Er du sikker på at du vil synliggjøre alle kommende arrangementer?
                  <br />
                  <br />
                  Arrangementer som er skjult vil bli synlig overalt (både i app og på nettside).
                  <br />
                  Arrangementer som allerede er synlig i app eller på nettside vil forbli slik.
                </div>
              </>
            }
            onConfirm={handleMakeVisible}
            okText="Ja, synliggjør"
            cancelText="Avbryt"
            okType="primary"
          >
            <Button type="primary" block>
              Synliggjør fremtidige arrangementer
            </Button>
          </Popconfirm>
        </div>
      )}
    </Card>
  );
};

export default EventsTable;
